import {
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid2,
  IconButton,
  Stack,
  Switch,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";

import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import DashboardFooter from "../../../layoutcontainers/DashboardLayout/components/Footer";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialInput from "../../../components/MaterialInput";
import MaterialSelect from "../../../components/MasterSelect";
import MaterialButton from "../../../components/MaterialButton";
import MaterialBox from "../../../components/MaterialBox";
import { useSnackbar } from "../../../context/popup";

import { fetchData, fetchDataById, postData, putData } from "../../../services";

import { useAPIendpointsFormState } from "./components/APIendpointsStateHandle";
import DropDownConfig from "../../Utils/DropDownConfig";

const APIendpointsForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();

  const recordId = searchParams.get("id");
  const pageType = searchParams.get("pageType");

  const pageMode =
    pageType === "view" ? "view" : pageType === "edit" ? "edit" : "add";

  const { formData, setFormData, handleChange, checkAllFields } =
    useAPIendpointsFormState();
    

  /* =====================
     DROPDOWN OPTIONS
  ====================== */
  const [apiRegistryOptions, setApiRegistryOptions] = useState([]);
  const [environmentOptions, setEnvironmentOptions] = useState([]);
  const [protocolOptions, setProtocolOptions] = useState([]);
  const [authTypeOptions, setAuthTypeOptions] = useState([]);

  /* =====================
     FETCH DROPDOWNS
  ====================== */
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const results = await Promise.allSettled([
          fetchData("api-engine/api-registry?dropdown=true"),
          fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiEnvironment}`),
          fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiProtocol}`),
          fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiAuthType}`),
        ]);

        const [apiRegistryRes, environmentRes, protocolRes, authTypeRes] =
          results;

        if (
          apiRegistryRes.status === "fulfilled" &&
          apiRegistryRes.value?.success
        ) {
          setApiRegistryOptions(
            apiRegistryRes.value.data?.items?.map((i) => ({
              label: i.name,
              value: i.id,
            })) || []
          );
        }

        if (
          environmentRes.status === "fulfilled" &&
          environmentRes.value?.success
        ) {
          setEnvironmentOptions(
            environmentRes.value.data?.items?.map((i) => ({
              label: i.keyName,
              value: i.id,
            })) || []
          );
        }

        if (protocolRes.status === "fulfilled" && protocolRes.value?.success) {
          setProtocolOptions(
            protocolRes.value.data?.items?.map((i) => ({
              label: i.keyName,
              value: i.id,
            })) || []
          );
        }

        if (authTypeRes.status === "fulfilled" && authTypeRes.value?.success) {
          setAuthTypeOptions(
            authTypeRes.value.data?.items?.map((i) => ({
              label: i.keyName,
              value: i.id,
            })) || []
          );
          // Optional warning if any API failed
          if (results.some((r) => r.status === "rejected")) {
            showSnackbar("Some dropdowns failed to load", "warning");
          }
        }
      } catch (err) {
        showSnackbar("Failed to load dropdowns", "error");
      }
    };

    fetchDropdowns();
  }, []);

  /* =====================
     EDIT MODE FETCH
  ====================== */
  useEffect(() => {
    if (!recordId) return;

    const fetchDetails = async () => {
      const res = await fetchDataById(`/api-engine/api-endpoint/${recordId}`);

      if (res?.success) {
        const d = res.data;

        setFormData((prev) => ({
          ...prev,

          /* ================= SELECT FIELDS ================= */

          apiRegistryId: d.apiRegistry
            ? { label: d.apiRegistry.name, value: d.apiRegistry.id }
            : null,

          environmentId: d.environment
            ? { label: d.environment.keyName, value: d.environment.id }
            : null,

          protocolId: d.protocol
            ? { label: d.protocol.keyName, value: d.protocol.id }
            : null,

          authTypeId: d.authType
            ? { label: d.authType.keyName, value: d.authType.id }
            : null,

          /* ================= URL FIELDS ================= */

          endpointUrl: d.endpointUrl ?? "",
          wsdlUrl: d.wsdlUrl ?? "",
          graphqlSchemaLocation: d.graphqlSchemaLocation ?? "",
          openapiSpecUrl: d.openapiSpecUrl
            ? d.openapiSpecUrl.replace(/"$/, "")
            : "",
          webhookUrl: d.webhookUrl ?? "",

          operationsConfig: d?.operationsConfig && Object.keys(d.operationsConfig).length > 0
            ? JSON.stringify(d.operationsConfig, null, 2)
            : "",

          /* ================= AUTH CONFIG ================= */

          clientId: d.authConfig?.clientId ?? "",
          clientSecret: d.authConfig?.clientSecret ?? "",
          tokenUrl: d.authConfig?.tokenUrl ?? "",

          /* ================= CONFIG ================= */

          timeoutMs: d.timeoutMs ?? 0,

          maxRequests: d.rateLimitConfig?.maxRequests ?? 0,
          perMilliseconds: d.rateLimitConfig?.perMilliseconds ?? 0,

          maxRetries: d.retryPolicy?.maxRetries ?? 0,
          backoffStrategy: d.retryPolicy?.backoffStrategy ?? "",

          isActive: d.isActive ?? true,

          /* ================= STATUS FLAGS ================= */

          apiRegistryIdStatus: "success",
          environmentIdStatus: "success",
          protocolIdStatus: "success",
          authTypeIdStatus: "success",
          endpointUrlStatus: "success",
          timeoutMsStatus: "success",
        }));
      }
    };

    fetchDetails();
  }, [recordId]);

  /* =====================
     SUBMIT
  ====================== */

  const handleSubmit = async () => {
    const payload = {
      apiRegistryId: formData.apiRegistryId?.value,
      environmentId: formData.environmentId?.value,
      endpointUrl: formData.endpointUrl,
      protocolId: formData.protocolId?.value,

      wsdlUrl: formData.wsdlUrl,
      graphqlSchemaLocation: formData.graphqlSchemaLocation,
      openapiSpecUrl: formData.openapiSpecUrl,
      webhookUrl: formData.webhookUrl,

      operationsConfig: formData.operationsConfig ? JSON.parse(formData.operationsConfig) : {},

      authTypeId: formData.authTypeId?.value,
      authConfig: {
        clientId: formData.clientId,
        clientSecret: formData.clientSecret,
        tokenUrl: formData.tokenUrl,
      },

      timeoutMs: Number(formData.timeoutMs),
      rateLimitConfig: {
        maxRequests: Number(formData.maxRequests),
        perMilliseconds: Number(formData.perMilliseconds),
      },
      retryPolicy: {
        maxRetries: Number(formData.maxRetries),
        backoffStrategy: formData.backoffStrategy,
      },
      isActive: formData?.isActive,

    };

    try {
      const res = recordId
        ? await putData(`/api-engine/api-endpoint/${recordId}`, payload)
        : await postData("/api-engine/api-endpoint", payload);

      if (res?.success) {
        showSnackbar(res.message, "success");
        navigate(-1);
      } else {
        showSnackbar(res.message, "error");
      }
    } catch (e) {
      showSnackbar("Something went wrong", "error");
    }
  };

  /* =====================
     UI
  ====================== */
  return (
    <>
      <DashboardLayout>
        <Card sx={{ p: 2 }}>
          <CardHeader
            title={
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton onClick={() => navigate(-1)}>
                  <ArrowBack />
                </IconButton>
                <Stack direction="column" spacing={0}>
                  <MaterialTypography variant="h5">
                    External API Endpoints
                  </MaterialTypography>
                  <MaterialTypography variant="caption" color="text">
                    List of External API Endpoints
                  </MaterialTypography>
                </Stack>
              </Stack>
            }
          />

          <CardContent>
            <Grid2 container spacing={3}>
              {/* ================= BASIC API CONFIG ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} borderRadius="md" shadow="sm">
                  <MaterialTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={1}
                  >
                    Basic API Configuration
                  </MaterialTypography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        API Registry
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.apiRegistryId}
                        options={apiRegistryOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "apiRegistryId" })
                        }
                        success={formData.apiRegistryIdStatus === "success"}
                        error={formData.apiRegistryIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Environment
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.environmentId}
                        options={environmentOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "environmentId" })
                        }
                        success={formData.environmentIdStatus === "success"}
                        error={formData.environmentIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                      <MaterialTypography variant="caption">
                        Endpoint URL
                      </MaterialTypography>
                      <MaterialInput
                        name="endpointUrl"
                        value={formData.endpointUrl}
                        onChange={handleChange}
                        success={formData.endpointUrlStatus === "success"}
                        error={formData.endpointUrlStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>
                  </Grid2>
                </MaterialBox>
              </Grid2>

              {/* ================= PROTOCOL & SPEC ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} borderRadius="md" shadow="sm">
                  <MaterialTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={1}
                  >
                    Protocol & Specification
                  </MaterialTypography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Protocol
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.protocolId}
                        options={protocolOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "protocolId" })
                        }
                        success={formData.protocolIdStatus === "success"}
                        error={formData.protocolIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        WSDL URL
                      </MaterialTypography>
                      <MaterialInput
                        name="wsdlUrl"
                        value={formData.wsdlUrl}
                        onChange={handleChange}
                        success={formData.wsdlUrlStatus === "success"}
                        error={formData.wsdlUrlStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        GraphQL Schema
                      </MaterialTypography>
                      <MaterialInput
                        name="graphqlSchemaLocation"
                        value={formData.graphqlSchemaLocation}
                        onChange={handleChange}
                        success={
                          formData.graphqlSchemaLocationStatus === "success"
                        }
                        error={formData.graphqlSchemaLocationStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        OpenAPI Spec URL
                      </MaterialTypography>
                      <MaterialInput
                        name="openapiSpecUrl"
                        value={formData.openapiSpecUrl}
                        onChange={handleChange}
                        success={formData.openapiSpecUrlStatus === "success"}
                        error={formData.openapiSpecUrlStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                      <MaterialTypography variant="caption">
                        Webhook URL
                      </MaterialTypography>
                      <MaterialInput
                        name="webhookUrl"
                        value={formData.webhookUrl}
                        onChange={handleChange}
                        success={formData.webhookUrlStatus === "success"}
                        error={formData.webhookUrlStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>
                  </Grid2>
                </MaterialBox>
              </Grid2>

              {/* ================= OPERATIONS ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} borderRadius="md" shadow="sm">
                  <MaterialTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={1}
                  >
                    Operations Configuration
                  </MaterialTypography>

                  <MaterialInput
                    name="operationsConfig"
                    value={formData.operationsConfig}
                    onChange={handleChange}
                    success={formData.operationsConfigStatus === "success"}
                    error={formData.operationsConfigStatus === "error"}
                    disabled={pageMode === "view"}
                    multiline
                    rows={4}
                  />
                </MaterialBox>
              </Grid2>

              {/* ================= AUTH ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} borderRadius="md" shadow="sm">
                  <MaterialTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={1}
                  >
                    Authentication
                  </MaterialTypography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Auth Type
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.authTypeId}
                        options={authTypeOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "authTypeId" })
                        }
                        success={formData.authTypeIdStatus === "success"}
                        error={formData.authTypeIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Client ID
                      </MaterialTypography>
                      <MaterialInput
                        name="clientId"
                        value={formData.clientId}
                        onChange={handleChange}
                        success={formData.clientIdStatus === "success"}
                        error={formData.clientIdStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Client Secret
                      </MaterialTypography>
                      <MaterialInput
                        name="clientSecret"
                        type="password"
                        value={formData.clientSecret}
                        onChange={handleChange}
                        success={formData.clientSecretStatus === "success"}
                        error={formData.clientSecretStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Token URL
                      </MaterialTypography>
                      <MaterialInput
                        name="tokenUrl"
                        value={formData.tokenUrl}
                        onChange={handleChange}
                        success={formData.tokenUrlStatus === "success"}
                        error={formData.tokenUrlStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>
                  </Grid2>
                </MaterialBox>
              </Grid2>

              {/* ================= RATE LIMIT & RETRY ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} borderRadius="md" shadow="sm">
                  <MaterialTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={1}
                  >
                    Rate Limit & Retry Policy
                  </MaterialTypography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <MaterialTypography variant="caption">
                        Timeout (ms)
                      </MaterialTypography>
                      <MaterialInput
                        name="timeoutMs"
                        value={formData.timeoutMs}
                        onChange={handleChange}
                        success={formData.timeoutMsStatus === "success"}
                        error={formData.timeoutMsStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <MaterialTypography variant="caption">
                        Max Requests
                      </MaterialTypography>
                      <MaterialInput
                        name="maxRequests"
                        value={formData.maxRequests}
                        onChange={handleChange}
                        success={formData.maxRequestsStatus === "success"}
                        error={formData.maxRequestsStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <MaterialTypography variant="caption">
                        Per (ms)
                      </MaterialTypography>
                      <MaterialInput
                        name="perMilliseconds"
                        value={formData.perMilliseconds}
                        onChange={handleChange}
                        success={formData.perMillisecondsStatus === "success"}
                        error={formData.perMillisecondsStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Max Retries
                      </MaterialTypography>
                      <MaterialInput
                        name="maxRetries"
                        value={formData.maxRetries}
                        onChange={handleChange}
                        success={formData.maxRetriesStatus === "success"}
                        error={formData.maxRetriesStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Backoff Strategy
                      </MaterialTypography>
                      <MaterialInput
                        name="backoffStrategy"
                        value={formData.backoffStrategy}
                        onChange={handleChange}
                        success={formData.backoffStrategyStatus === "success"}
                        error={formData.backoffStrategyStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>
                  </Grid2>
                </MaterialBox>
              </Grid2>

              {/* ================= STATUS ================= */}
              <Grid2 size={{ xs: 12 }} sx={{ px: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "isActive",
                            value: e.target.checked
                          },
                        })
                      }
                      disabled={pageMode === "view"}
                    />
                  }
                  label={formData.isActive ? "Active" : "Inactive"}
                />
              </Grid2>
            </Grid2>
          </CardContent>

          {pageMode !== "view" && (
            <MaterialBox display="flex" justifyContent="flex-end">
              <MaterialButton
                variant="gradient"
                color="dark"
                disabled={!checkAllFields()}
                onClick={handleSubmit}
              >
                {pageMode === "edit" ? "Update" : "Submit"}
              </MaterialButton>
            </MaterialBox>
          )}
        </Card>
      </DashboardLayout>

      <DashboardFooter />
    </>
  );
};

export default APIendpointsForm;
